if (!customElements.get('card-variant-selector')) {
  class CardVariantSelector extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('.variant-selector__option');
      this.card = this.closest('.product-card');
      this.priceContainer = this.card?.querySelector('[data-card-price]');
      this.form = this.card?.querySelector('.quick-add-product-form');
      this.variantInput = this.form?.querySelector('.product-variant-id');
      this.submitButton = this.form?.querySelector('[type="submit"]');
      this.badgeContainer = this.card?.querySelector('.card__badges');
      this.mediaContainer = this.card?.querySelector('.card__media .media');
      this.primaryImage = this.mediaContainer?.querySelector('img:first-child');
      this.cardLinks = this.card?.querySelectorAll('.card__product-link');

      this.bindEvents();
    }

    bindEvents() {
      this.buttons.forEach((button) => {
        button.addEventListener('click', this.onVariantSelect.bind(this));
      });
    }

    onVariantSelect(event) {
      const button = event.currentTarget;
      const variantId = button.dataset.variantId;
      const variantAvailable = button.dataset.variantAvailable === 'true';

      this.updateSelectedButton(button);
      this.updatePrice(variantId);
      this.updateForm(variantId, variantAvailable);
      this.updateBadges(variantId);
      this.updateImage(variantId);
      this.updateLinks(variantId);
    }

    updateSelectedButton(selectedButton) {
      this.buttons.forEach((button) => {
        button.classList.remove('variant-selector__option--selected');
        button.setAttribute('aria-pressed', 'false');
      });
      selectedButton.classList.add('variant-selector__option--selected');
      selectedButton.setAttribute('aria-pressed', 'true');
    }

    updatePrice(variantId) {
      const template = this.card?.querySelector(`template[data-variant-price="${variantId}"]`);
      if (!template || !this.priceContainer) return;

      const newPrice = template.content.cloneNode(true).querySelector('[data-card-price]');
      if (!newPrice) return;

      this.priceContainer.replaceWith(newPrice);
      this.priceContainer = newPrice;
    }

    updateForm(variantId, available) {
      if (!this.variantInput || !this.submitButton) return;

      this.variantInput.value = variantId;

      if (available) {
        this.variantInput.disabled = false;
        this.submitButton.disabled = false;
      } else {
        this.variantInput.disabled = true;
        this.submitButton.disabled = true;
      }
    }

    updateBadges(variantId) {
      const template = this.card?.querySelector(`template[data-variant-badges="${variantId}"]`);
      if (!template || !this.badgeContainer) return;

      this.badgeContainer.innerHTML = '';
      this.badgeContainer.appendChild(template.content.cloneNode(true));
    }

    updateImage(variantId) {
      const template = this.card?.querySelector(`template[data-variant-image="${variantId}"]`);
      if (!template || !this.mediaContainer || !this.primaryImage) return;

      const newImage = template.content.cloneNode(true).querySelector('img');
      if (!newImage) return;

      // Add loading state to current image
      this.primaryImage.style.opacity = '0.95';
      this.primaryImage.style.transition = 'opacity 0.2s ease';

      // Prepare new image (hidden, absolute positioned, with transition)
      newImage.classList.add('card__image--loading');
      newImage.loading = 'eager';

      // Insert new image into DOM (this triggers loading even with lazy)
      this.mediaContainer.appendChild(newImage);

      const completeSwap = () => {
        // Fade in new image while old image is still visible
        requestAnimationFrame(() => {
          newImage.style.opacity = '1';
        });

        // After fade completes, remove old image and clean up new image styles
        setTimeout(() => {
          this.primaryImage.remove();
          newImage.classList.remove('card__image--loading');
          newImage.style.opacity = '';
          this.primaryImage = newImage;
        }, 300);
      };

      // If image is already loaded (cached), swap immediately
      if (newImage.complete && newImage.naturalHeight !== 0) {
        completeSwap();
      } else {
        // Otherwise wait for load
        newImage.addEventListener('load', completeSwap, { once: true });
        newImage.addEventListener('error', () => {
          // Remove failed image and restore opacity
          newImage.remove();
          this.primaryImage.style.opacity = '1';
        }, { once: true });
      }
    }

    updateLinks(variantId) {
      if (!this.cardLinks) return;

      this.cardLinks.forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('variant', variantId);
        link.href = url.toString();
      });
    }
  }

  customElements.define('card-variant-selector', CardVariantSelector);
}
